import { Directive, ElementRef, computed, inject, input } from '@angular/core'
import { ElevioService } from '../../services/elevio/elevio.service'

export const ELEVIO_HOME_BASE_URL = 'https://ftr.elevio.help'

/**
 * Opens the Elevio side-panel widget on click.
 * Provide `articleId` to open a specific article; omit it to open the help center.
 *
 * When attached to an `<a>` element, the directive sets the `href` to the article's URL
 * so that clicks gracefully degrade to opening the article in a new tab if the widget is unavailable.
 *
 * @example
 * ``` html
 * <a ftrElevioTrigger articleId="368">Learn more</a>
 * <button ftrElevioTrigger>Help Center</button>
 * ```
 */
@Directive({
  selector: '[ftrElevioTrigger]',
  standalone: true,
  host: {
    'data-track': '',
    'data-track-group': 'Support',
    '(click)': 'onClick($event)',
    '[attr.href]': 'isAnchor ? articleLink() : null',
    '[attr.target]': 'isAnchor ? "_blank" : null',
    '[attr.rel]': 'isAnchor ? "noopener" : null',
    style: 'cursor: pointer',
  },
})
export class ElevioTriggerDirective {
  private readonly elevioService = inject(ElevioService)
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement

  protected readonly isAnchor = this.element instanceof HTMLAnchorElement

  /**
   * Grab the ID in Elevio, or directly from the article's URL: `https://ftr.elevio.help/articles/{articleId}`.
   *
   * An unknown ID shows a "Sorry. Please try again." message, so make sure a real article
   * is reachable before this becomes user-facing.
   */
  readonly articleId = input<string>()

  protected onClick(event: Event): void {
    // this will allow <a> element hosts to open the article in a new tab as fallback
    if (!this.elevioService.isWidgetAvailable()) {
      return
    }

    if (this.isAnchor) {
      event.preventDefault()
    }

    this.open()
  }

  protected readonly articleLink = computed(() => {
    const articleId = this.articleId()
    return articleId ? `${ELEVIO_HOME_BASE_URL}/articles/${articleId}` : ELEVIO_HOME_BASE_URL
  })

  private open(): void {
    const articleId = this.articleId()
    if (articleId) {
      this.elevioService.openArticle(articleId)
    } else {
      this.elevioService.openHome()
    }
  }
}
